import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Slider } from './ui/slider';
import { districtsApi } from '../services/api';
import { Search, SlidersHorizontal, X, Home, Building2, Store } from 'lucide-react';

const SearchFilters = ({ filters, setFilters, onSearch }) => {
  const { t, getLocalizedText } = useLanguage();
  const [showFilters, setShowFilters] = useState(false);
  const [districts, setDistricts] = useState([]);

  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const data = await districtsApi.getAll();
        setDistricts(data);
      } catch (error) {
        console.error('Error fetching districts:', error);
      }
    };
    fetchDistricts();
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      type: 'all',
      district: 'all',
      priceType: 'all',
      minPrice: 0,
      maxPrice: 1000000,
      bedrooms: 'any',
    });
  };

  const propertyTypes = [
    { value: 'all', label: t('allTypes'), icon: null },
    { value: 'apartment', label: t('apartments'), icon: Building2 },
    { value: 'house', label: t('houses'), icon: Home },
    { value: 'commercial', label: t('commercial'), icon: Store },
  ];

  return (
    <div className="bg-white rounded-2xl shadow-xl p-4 md:p-6 border border-gray-100">
      {/* Main Search Bar */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            placeholder={t('searchPlaceholder')}
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="pl-12 h-12 bg-gray-50 border-gray-200 rounded-xl text-base"
          />
        </div>
        
        <Select value={filters.district} onValueChange={(v) => handleFilterChange('district', v)}>
          <SelectTrigger className="w-full md:w-48 h-12 bg-gray-50 border-gray-200 rounded-xl">
            <SelectValue placeholder={t('selectDistrict')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('allTypes')}</SelectItem>
 {(districts || []).map((district) => (
              <SelectItem key={district.district_id || district.id} value={district.district_id || district.id}>
                {getLocalizedText(district.name)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          onClick={() => setShowFilters(!showFilters)}
          className="h-12 px-4 rounded-xl border-gray-200 gap-2"
        >
          <SlidersHorizontal className="w-4 h-4" />
          {t('filters')}
        </Button>

        <Button
          onClick={onSearch}
          className="h-12 px-8 bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
        >
          {t('search')}
        </Button>
      </div>

      {/* Property Type Quick Filters */}
      <div className="flex flex-wrap gap-2 mt-4">
        {propertyTypes.map((type) => (
          <button
            key={type.value}
            onClick={() => handleFilterChange('type', type.value)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
              filters.type === type.value
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {type.icon && <type.icon className="w-4 h-4" />}
            {type.label}
          </button>
        ))}
      </div>

      {/* Expanded Filters */}
      {showFilters && (
        <div className="mt-6 pt-6 border-t border-gray-100 space-y-6 animate-in slide-in-from-top duration-300">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Sale/Rent Toggle */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Sale / Rent</label>
              <div className="flex gap-2">
                {['all', 'sale', 'rent'].map((type) => (
                  <button
                    key={type}
                    onClick={() => handleFilterChange('priceType', type)}
                    className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                      filters.priceType === type
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {type === 'all' ? t('allTypes') : type === 'sale' ? t('forSale') : t('forRent')}
                  </button>
                ))}
              </div>
            </div>

            {/* Bedrooms */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">{t('bedrooms')}</label>
              <div className="flex gap-2">
                {['any', '1', '2', '3', '4+'].map((num) => (
                  <button
                    key={num}
                    onClick={() => handleFilterChange('bedrooms', num)}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      filters.bedrooms === num
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {num === 'any' ? 'All' : num}
                  </button>
                ))}
              </div>
            </div>

            {/* Price Range */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                {t('priceRange')}: ${filters.minPrice.toLocaleString()} - ${filters.maxPrice.toLocaleString()}
              </label>
              <Slider
                value={[filters.minPrice, filters.maxPrice]}
                onValueChange={([min, max]) => {
                  handleFilterChange('minPrice', min);
                  handleFilterChange('maxPrice', max);
                }}
                max={1000000}
                step={10000}
                className="mt-4"
              />
            </div>
          </div>

          {/* Clear Filters Button */}
          <div className="flex justify-end">
            <Button
              variant="ghost"
              onClick={clearFilters}
              className="text-gray-500 hover:text-gray-700 gap-2"
            >
              <X className="w-4 h-4" />
              Clear Filters
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchFilters;
